import CategoryIcon from 'owa-categories/lib/components/CategoryIcon';
import {
    getCategoryUnreadCount,
    getCategoryTotalCount,
} from 'owa-categories/lib/utils/getCategoryCounts';
import { getMasterOrSharedCategoryList } from 'owa-categories/lib/utils/getMasterOrSharedCategoryList';
import { observer } from 'owa-mobx-react';
import DragAndDroppable from 'owa-dnd/lib/components/DragAndDroppable';
import type DropViewState from 'owa-dnd/lib/store/schema/DropViewState';
import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import type { FavoriteNodeDragData } from 'owa-favorites-types';
import { type FolderForestNodeType } from 'owa-favorites-types';
import { showFavoritesContextMenu } from 'owa-mail-favorites-store/lib/actions/favoritesContextMenu';
import { lazySelectFavoriteCategory } from 'owa-mail-folder-forest-actions';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { DRAG_X_OFFSET, DRAG_Y_OFFSET } from 'owa-mail-folder-view';
import { buildUnreadTotalCountTooltip } from 'owa-folders';
import { getAnchorForContextMenu } from 'owa-positioning';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import type CategoryType from 'owa-service/lib/contract/CategoryType';
import TreeNode from 'owa-tree-node/lib/components/TreeNode';
import { UnreadReadCountBadge } from 'owa-unreadread-count-badge';
import React from 'react';
import { removeFavoriteCategory } from '../util/removeFavorite';
import { dragPreview } from './FavoriteNode.scss';

import type { FavoriteNodeCommonProps } from './FavoriteNode';
import { getFavoriteCategoryDisplayNameForMultiAccount } from '../util/getFavoriteCategoryDisplayNameForMultiAccount';
import type { MailboxInfo } from 'owa-client-types';
import { SourceMap } from 'owa-folders-constants';
import {
    canDropFavorite,
    canDropFolder,
    onDropFolder,
    onDropFavorite,
    onDropMailListRowsToCategory,
    onDropMailListItemPartToCategory,
} from '../util/dragDropUtils';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

export interface FavoriteCategoryNodeProps extends FavoriteNodeCommonProps {
    categoryId: string;
    dropViewState: DropViewState;
    mailboxInfo: MailboxInfo;
    ellipsesOnHover?: boolean;
}

const defaultExport: React.FunctionComponent<FavoriteCategoryNodeProps> = observer(
    function FavoriteCategoryNode(props: FavoriteCategoryNodeProps) {
        const mailboxInfo = props.mailboxInfo;

        const category = useComputedValue((): CategoryType => {
            return getMasterOrSharedCategoryList(mailboxInfo).filter(
                categoryType => categoryType.Id === props.categoryId
            )[0];
        }, [props.categoryId, mailboxInfo]);

        const isSelected = useComputedValue((): boolean => {
            return props.categoryId == getSelectedNode().id;
        }, [props.categoryId]);

        const unreadCount = useComputedValue((): number => {
            return getCategoryUnreadCount(category?.Name ?? '', mailboxInfo);
        }, [mailboxInfo]);

        const totalCount = useComputedValue((): number => {
            return getCategoryTotalCount(category?.Name ?? '', mailboxInfo);
        }, [mailboxInfo]);

        const renderUnreadCount = React.useCallback((): React.JSX.Element | null => {
            return unreadCount ? <UnreadReadCountBadge count={unreadCount} /> : null;
        }, [unreadCount]);
        const renderCustomIcon = React.useCallback(
            (customIconClassNames: string): React.JSX.Element => {
                return (
                    <CategoryIcon
                        categoryName={category?.Name}
                        iconClassName={customIconClassNames}
                        mailboxInfo={mailboxInfo}
                    />
                );
            },
            [category?.Name, mailboxInfo]
        );
        const onClick = React.useCallback(
            (evt: React.MouseEvent<unknown>) => {
                evt.stopPropagation();
                lazySelectFavoriteCategory.importAndExecute(props.categoryId);
            },
            [props.categoryId]
        );
        const onContextMenu = React.useCallback(
            (evt: React.MouseEvent<HTMLElement>) => {
                evt.stopPropagation();
                evt.preventDefault();
                showFavoritesContextMenu(
                    props.favoriteId,
                    4,
                    getAnchorForContextMenu(evt),
                    undefined /* folderId */,
                    undefined /* showRootMenu */,
                    undefined /* isPlaceholderNode */,
                    props.actionSource
                );
            },
            [props.favoriteId, props.actionSource]
        );
        // VSO 23480 - [FavoritesDND]Refactor the common DND code into a base component
        const getDragData = React.useCallback(() => {
            const folderNodeDragData: FavoriteNodeDragData = {
                itemType: DraggableItemTypes.FavoriteNode,
                favoriteId: props.favoriteId,
                favoriteType: 4,
                displayName: category?.Name ?? '',
                itemData: {
                    mailboxInfo,
                },
            };
            return folderNodeDragData;
        }, [props.favoriteId, category?.Name, mailboxInfo]);
        const canDrop = React.useCallback(
            (dragData: DragData) => {
                switch (dragData.itemType) {
                    case DraggableItemTypes.FavoriteNode:
                        return canDropFavorite(dragData, mailboxInfo);
                    case DraggableItemTypes.MailFolderNode:
                        return canDropFolder(dragData, mailboxInfo);
                    case DraggableItemTypes.MailListItemPart:
                    case DraggableItemTypes.MailListRow:
                    case DraggableItemTypes.MultiMailListMessageRows:
                    case DraggableItemTypes.MultiMailListConversationRows:
                        // Allow dropping mail items on category nodes
                        return isGlobalFeatureEnabled('tri-dragDrop-mlToCategory');
                    default:
                        return false;
                }
            },
            [mailboxInfo]
        );
        const onDrop = React.useCallback(
            async (dragData: DragData) => {
                const itemType = dragData.itemType;
                switch (itemType) {
                    case DraggableItemTypes.MailFolderNode:
                        await onDropFolder(
                            dragData,
                            mailboxInfo,
                            props.favoriteId,
                            props.actionSource
                        );
                        break;
                    case DraggableItemTypes.FavoriteNode:
                        await onDropFavorite(dragData, props.favoriteId, props.actionSource);
                        break;
                    case DraggableItemTypes.MailListItemPart:
                        await onDropMailListItemPartToCategory(
                            dragData,
                            category?.Name ?? '',
                            mailboxInfo
                        );
                        break;
                    case DraggableItemTypes.MailListRow:
                    case DraggableItemTypes.MultiMailListMessageRows:
                    case DraggableItemTypes.MultiMailListConversationRows:
                        await onDropMailListRowsToCategory(
                            dragData,
                            category?.Name ?? '',
                            mailboxInfo
                        );
                        break;
                }
            },
            [mailboxInfo, props.favoriteId, category?.Name]
        );

        const toggleFavorite = React.useCallback(() => {
            removeFavoriteCategory(
                props.categoryId,
                mailboxInfo,
                props.actionSource ?? 'ContextCategory'
            );
        }, [props.categoryId, mailboxInfo, props.actionSource]);

        const displayName = useComputedValue(
            () =>
                getFavoriteCategoryDisplayNameForMultiAccount(
                    category?.Name ?? '',
                    mailboxInfo,
                    false /* useBothIdentifiers, both being account nickname and email */
                ),
            [category?.Name, mailboxInfo]
        );
        const tooltipDisplayName = useComputedValue(
            () =>
                getFavoriteCategoryDisplayNameForMultiAccount(
                    category?.Name ?? '',
                    mailboxInfo,
                    true /* useBothIdentifiers, both being account nickname and email */
                ),
            [category?.Name, mailboxInfo]
        );

        const showAsHoverOnDroppedOver = useComputedValue((): boolean => {
            const draggableItemType = props.dropViewState.draggableItemType;
            return (
                draggableItemType === DraggableItemTypes.MailListRow ||
                draggableItemType === DraggableItemTypes.MultiMailListConversationRows ||
                draggableItemType === DraggableItemTypes.MultiMailListMessageRows ||
                draggableItemType === DraggableItemTypes.MailListItemPart
            );
        }, [props.dropViewState.draggableItemType]);

        // If the categoryId does not exist within the MCL early return
        // This can happen in edge cases such as when a category is favorited and then immediately deleted from the MCL
        if (category === undefined) {
            return null;
        }

        return (
            <DragAndDroppable
                getDragData={getDragData}
                getDragPreview={getDragPreview}
                xOffset={DRAG_X_OFFSET}
                yOffset={DRAG_Y_OFFSET}
                dropViewState={props.dropViewState}
                onDrop={onDrop}
                canDrop={canDrop}
            >
                <TreeNode
                    onRenderCustomIcon={renderCustomIcon}
                    displayName={displayName}
                    isRootNode={false}
                    key={props.categoryId}
                    isDroppedOver={props.dropViewState.isDragOver}
                    showAsHoverOnDroppedOver={showAsHoverOnDroppedOver}
                    onClick={onClick}
                    isSelected={isSelected}
                    onContextMenu={onContextMenu}
                    renderRightCharm={renderUnreadCount}
                    isFavorited={true}
                    toggleFavorite={toggleFavorite}
                    customTreeNodeTooltip={buildUnreadTotalCountTooltip(
                        tooltipDisplayName,
                        totalCount,
                        unreadCount
                    )}
                    ellipsesOnHover={props.ellipsesOnHover}
                    isVerticalNode={props.isVerticalNode}
                    hideIcon={props.hideIcon}
                    source={SourceMap.favorite}
                    shouldShowFocusBorder={true}
                    mailboxInfo={mailboxInfo}
                    useNewSelectionUI={isGlobalFeatureEnabled('fp-newfoldersselection-ui')}
                />
            </DragAndDroppable>
        );
    },
    'FavoriteCategoryNode'
);
export default defaultExport;

function getDragPreview(dragData: DragData) {
    const elem = document.createElement('div');
    elem.className = dragPreview;
    elem.innerText = (dragData as FavoriteNodeDragData)?.displayName ?? '';
    return elem;
}
